import React, { useState } from 'react';
import { MessageSquare, Send, Search, Filter, User, Clock, CheckCircle2, AlertCircle, Eye, Plus, X, Reply, Building2, Mail, Edit, Trash2 } from 'lucide-react';

const PharmacistMessaging = () => {
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [newMessage, setNewMessage] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });

  const [editMessage, setEditMessage] = useState({
    subject: '',
    message: '',
    priority: 'medium'
  });

  // Messages envoyés par le pharmacien
  const myMessages = [
    {
      id: 1,
      subject: 'Problème de connexion au système',
      message: 'Bonjour, depuis ce matin je n\'arrive plus à me connecter à la plateforme. J\'obtiens une erreur "Session expirée" à chaque tentative. Pouvez-vous m\'aider ? Merci.',
      status: 'pending',
      priority: 'high',
      timestamp: '2024-11-04T09:30:00',
      replied: false,
      reply: null
    },
    {
      id: 2,
      subject: 'Demande d\'ajout de pharmacien',
      message: 'Bonjour, j\'ai embauché un nouveau pharmacien, Dr. Mamadou Sall. Pourriez-vous créer son compte sur la plateforme ? Je vous envoie ses informations par email séparé. Merci d\'avance.',
      status: 'in_progress',
      priority: 'medium',
      timestamp: '2024-11-04T08:15:00',
      replied: false,
      reply: null
    },
    {
      id: 3,
      subject: 'Question sur le planning de garde',
      message: 'Bonjour, je voulais savoir comment modifier le planning de garde pour la semaine prochaine. Ma pharmacie ne pourra pas assurer la garde mercredi soir à cause de travaux. Merci.',
      status: 'resolved',
      priority: 'low',
      timestamp: '2024-11-03T14:20:00',
      replied: true,
      reply: {
        text: 'Bonjour Dr. Ndiaye, vous pouvez modifier le planning directement depuis votre espace. Allez dans "Planning de garde" > Sélectionnez la semaine > Cliquez sur "Modifier". Si vous avez besoin d\'aide, n\'hésitez pas. Cordialement.',
        timestamp: '2024-11-03T15:00:00',
        repliedBy: 'Dr. Touré (Admin)'
      }
    },
    {
      id: 4,
      subject: 'Mise à jour des horaires',
      message: 'Bonjour, suite à notre discussion téléphonique, je confirme que mes nouveaux horaires sont 7h30 - 21h30 du lundi au samedi. Pourriez-vous mettre à jour sur le système ? Merci beaucoup.',
      status: 'resolved',
      priority: 'low',
      timestamp: '2024-11-03T11:45:00',
      replied: true,
      reply: {
        text: 'Bonjour Dr. Ndiaye, vos horaires ont bien été mis à jour dans le système. Les patients pourront voir vos nouveaux horaires dès maintenant. Bonne continuation !',
        timestamp: '2024-11-03T12:10:00',
        repliedBy: 'Dr. Touré (Admin)'
      }
    },
    {
      id: 5,
      subject: 'Demande de formation',
      message: 'Bonjour, j\'aimerais suivre une formation sur les nouvelles fonctionnalités de la plateforme. Quand sera organisée la prochaine session ? Merci.',
      status: 'resolved',
      priority: 'medium',
      timestamp: '2024-11-02T16:30:00',
      replied: true,
      reply: {
        text: 'Bonjour Dr. Ndiaye, avec plaisir ! Je vous propose une session de formation en visioconférence mardi prochain à 14h. Je vous envoie le lien de connexion par email. À bientôt !',
        timestamp: '2024-11-02T17:15:00',
        repliedBy: 'Mariama Diop (Assistante)'
      }
    },
    {
      id: 6,
      subject: 'Rapport d\'activité mensuel',
      message: 'Bonjour, je n\'arrive pas à générer le rapport d\'activité du mois dernier. Le système affiche une erreur. Pouvez-vous vérifier s\'il vous plaît ?',
      status: 'resolved',
      priority: 'medium',
      timestamp: '2024-11-01T10:20:00',
      replied: true,
      reply: {
        text: 'Bonjour Dr. Ndiaye, le problème a été corrigé. Vous pouvez maintenant générer votre rapport. Si vous rencontrez encore des difficultés, n\'hésitez pas à me recontacter.',
        timestamp: '2024-11-01T11:45:00',
        repliedBy: 'Dr. Touré (Admin)'
      }
    }
  ];

  const statusOptions = [
    { value: 'all', label: 'Tous mes messages' },
    { value: 'pending', label: 'En attente' },
    { value: 'in_progress', label: 'En cours' },
    { value: 'resolved', label: 'Résolus' }
  ];

  const priorityOptions = [
    { value: 'high', label: 'Haute', description: 'Urgent - Nécessite une attention immédiate' },
    { value: 'medium', label: 'Moyenne', description: 'Important - Dans les prochains jours' },
    { value: 'low', label: 'Basse', description: 'Information - Pas urgent' }
  ];

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'En attente', icon: AlertCircle, border: 'border-orange-200' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'En cours', icon: Clock, border: 'border-blue-200' },
      resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Résolu', icon: CheckCircle2, border: 'border-emerald-200' }
    };
    return badges[status] || badges.pending;
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      high: { bg: 'bg-red-100', text: 'text-red-700', label: 'Haute', border: 'border-red-200' },
      medium: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'Moyenne', border: 'border-orange-200' },
      low: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Basse', border: 'border-blue-200' }
    };
    return badges[priority] || badges.medium;
  };

  const filteredMessages = myMessages.filter(message => {
    const matchesSearch = 
      message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || message.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: myMessages.length,
    pending: myMessages.filter(m => m.status === 'pending').length,
    inProgress: myMessages.filter(m => m.status === 'in_progress').length,
    resolved: myMessages.filter(m => m.status === 'resolved').length
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Il y a quelques minutes';
    } else if (diffInHours < 24) {
      return `Il y a ${Math.floor(diffInHours)}h`;
    } else if (diffInHours < 48) {
      return 'Hier';
    } else {
      return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
    }
  };

  const handleNewMessageChange = (e) => {
    setNewMessage({
      ...newMessage,
      [e.target.name]: e.target.value
    });
  };

  const handleEditMessageChange = (e) => {
    setEditMessage({
      ...editMessage,
      [e.target.name]: e.target.value
    });
  };

  const handleSendMessage = () => {
    alert('Message envoyé avec succès !');
    setShowNewMessageModal(false);
    setNewMessage({ subject: '', message: '', priority: 'medium' });
  };

  const handleUpdateMessage = () => {
    alert('Message modifié avec succès !');
    setShowEditModal(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = (message) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce message ?')) {
      alert('Message supprimé avec succès !');
    }
  };

  const openNewMessageModal = () => {
    setNewMessage({ subject: '', message: '', priority: 'medium' });
    setShowNewMessageModal(true);
  };

  const openMessageModal = (message) => {
    setSelectedMessage(message);
    setShowMessageModal(true);
  };

  const openEditModal = (message) => {
    setSelectedMessage(message);
    setEditMessage({
      subject: message.subject,
      message: message.message,
      priority: message.priority
    });
    setShowEditModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-30">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgb(209, 213, 219) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      <main className="relative z-10 p-4 md:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl md:text-4xl font-black text-gray-800 mb-4">
            Mes Messages
          </h1>
          <p className="text-gray-600 text-lg">Communiquez avec l'administration SunuFarmasi</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <MessageSquare className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En attente</p>
                <p className="text-3xl font-bold text-orange-600">{stats.pending}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md">
                <AlertCircle className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">En cours</p>
                <p className="text-3xl font-bold text-blue-600">{stats.inProgress}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-md">
                <Clock className="text-white" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Résolus</p>
                <p className="text-3xl font-bold text-emerald-600">{stats.resolved}</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-md">
                <CheckCircle2 className="text-white" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Search and New Message */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher dans mes messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>

              <button
                onClick={openNewMessageModal}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-bold whitespace-nowrap"
              >
                <Plus size={20} />
                <span>Nouveau message</span>
              </button>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="space-y-4">
          {filteredMessages.map((message) => {
            const statusBadge = getStatusBadge(message.status);
            const priorityBadge = getPriorityBadge(message.priority);
            const StatusIcon = statusBadge.icon;

            return (
              <div key={message.id} className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all border border-gray-200 overflow-hidden">
                <div className="p-6">
                  {/* Message Header and Content */}
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-start space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                          <Mail className="text-white" size={24} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <h3 className="font-bold text-gray-800 text-lg">{message.subject}</h3>
                            <span className={`px-3 py-1 ${statusBadge.bg} ${statusBadge.text} rounded-full text-xs font-bold border ${statusBadge.border}`}>
                              <StatusIcon size={14} className="mr-1 inline" />
                              {statusBadge.label}
                            </span>
                            <span className={`px-3 py-1 ${priorityBadge.bg} ${priorityBadge.text} rounded-full text-xs font-bold border ${priorityBadge.border}`}>
                              {priorityBadge.label}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                            <span className="flex items-center">
                              <Clock size={14} className="mr-1" />
                              Envoyé {formatDate(message.timestamp)}
                            </span>
                          </div>

                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-2">{message.message}</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex md:flex-col gap-2">
                      <button
                        onClick={() => openMessageModal(message)}
                        className="flex-1 md:flex-none px-4 py-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all font-semibold text-sm flex items-center justify-center"
                      >
                        <Eye size={16} className="mr-2" />
                        Voir détails
                      </button>
                      
                      {/* Show Edit and Delete buttons only for unanswered messages */}
                      {!message.replied && (
                        <>
                          <button
                            onClick={() => openEditModal(message)}
                            className="flex-1 md:flex-none px-4 py-2 bg-orange-50 text-orange-600 rounded-xl hover:bg-orange-100 transition-all font-semibold text-sm flex items-center justify-center"
                          >
                            <Edit size={16} className="mr-2" />
                            Modifier
                          </button>
                          <button
                            onClick={() => handleDeleteMessage(message)}
                            className="px-4 py-2 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                      
                  {/* Reply Display - Shown directly in the list */}
                  {message.replied && message.reply && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 border-l-4 border-emerald-500">
                        <div className="flex items-start space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                            <Reply className="text-white" size={18} />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-sm font-bold text-emerald-800 flex items-center">
                                <User size={14} className="mr-1" />
                                Réponse de {message.reply.repliedBy}
                              </p>
                              <span className="text-xs text-gray-600 flex items-center">
                                <Clock size={12} className="mr-1" />
                                {formatDate(message.reply.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">{message.reply.text}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {filteredMessages.length === 0 && (
          <div className="text-center py-12 bg-white rounded-2xl shadow-lg border border-gray-200">
            <MessageSquare size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-semibold">Aucun message trouvé</p>
            <p className="text-gray-400 text-sm mt-2">Essayez de modifier vos filtres de recherche</p>
          </div>
        )}
      </main>

      {/* New Message Modal */}
      {showNewMessageModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full border border-gray-200">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center rounded-t-3xl">
              <h2 className="text-2xl font-black text-white">✉️ Nouveau message</h2>
              <button onClick={() => setShowNewMessageModal(false)} className="text-white hover:bg-white/20 p-2 rounded-xl transition-all">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Destinataire
                  </label>
                  <div className="px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-700">
                    <Building2 size={16} className="inline mr-2" />
                    Administration SunuFarmasi
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Priorité
                  </label>
                  <select
                    name="priority"
                    value={newMessage.priority}
                    onChange={handleNewMessageChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {priorityOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label} - {option.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Sujet *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={newMessage.subject}
                    onChange={handleNewMessageChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Problème de connexion, Demande d'information..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={newMessage.message}
                    onChange={handleNewMessageChange}
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    placeholder="Décrivez votre demande en détail..."
                  />
                </div>
              </div>

              <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => setShowNewMessageModal(false)}
                  className="flex-1 px-6 py-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-bold"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSendMessage}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-bold"
                >
                  <Send size={20} className="inline mr-2" />
                  Envoyer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Message Modal */}
      {showMessageModal && selectedMessage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-gray-200">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex justify-between items-center rounded-t-3xl sticky top-0 z-10">
              <h2 className="text-2xl font-black text-white">📨 Détails du message</h2>
              <button onClick={() => setShowMessageModal(false)} className="text-white hover:bg-white/20 p-2 rounded-xl transition-all">
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Message Info */}
              <div className="bg-gray-50 rounded-xl p-4 mb-6 border border-gray-200">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Destinataire:</span>
                    <span className="ml-2 font-semibold text-gray-800">Administration SunuFarmasi</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Envoyé le:</span>
                    <span className="ml-2 font-semibold text-gray-800">
                      {new Date(selectedMessage.timestamp).toLocaleDateString('fr-FR', { 
                        day: '2-digit', 
                        month: 'long', 
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Statut:</span>
                    <span className={`ml-2 px-2 py-1 ${getStatusBadge(selectedMessage.status).bg} ${getStatusBadge(selectedMessage.status).text} rounded-lg text-xs font-bold`}>
                      {getStatusBadge(selectedMessage.status).label}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-600">Priorité:</span>
                    <span className={`ml-2 px-2 py-1 ${getPriorityBadge(selectedMessage.priority).bg} ${getPriorityBadge(selectedMessage.priority).text} rounded-lg text-xs font-bold`}>
                      {getPriorityBadge(selectedMessage.priority).label}
                    </span>
                  </div>
                </div>
              </div>

              {/* Message Content */}
              <div className="mb-6">
                <h3 className="font-bold text-gray-800 text-xl mb-3">{selectedMessage.subject}</h3>
                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedMessage.message}</p>
                </div>
              </div>

              {/* Reply Section */}
              {selectedMessage.replied && selectedMessage.reply ? (
                <div>
                  <h3 className="font-bold text-gray-800 mb-3 flex items-center">
                    <CheckCircle2 size={20} className="mr-2 text-emerald-600" />
                    Réponse reçue
                  </h3>
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-5 border-l-4 border-emerald-500">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-bold text-emerald-800 flex items-center">
                        <User size={16} className="mr-2" />
                        {selectedMessage.reply.repliedBy}
                      </p>
                      <span className="text-xs text-gray-600 flex items-center">
                        <Clock size={14} className="mr-1" />
                        {formatDate(selectedMessage.reply.timestamp)}
                      </span>
                    </div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-line">{selectedMessage.reply.text}</p>
                  </div>
                </div>
              ) : (
                <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                  <div className="flex items-center space-x-3">
                    <AlertCircle size={24} className="text-orange-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-orange-800">En attente de réponse</p>
                      <p className="text-sm text-orange-700">L'administration vous répondra dans les plus brefs délais.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Close Button */}
              <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                <button
                  onClick={() => setShowMessageModal(false)}
                  className="px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:shadow-xl transition-all font-bold"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PharmacistMessaging;